/**
 * usePersonas — fetch the visible persona list (own + team + pinned).
 *
 * Cache indefinitely (staleTime Infinity) and rely on explicit invalidation
 * when personas are created/edited/favorited. Per CLAUDE.md, arbitrary TTLs
 * are forbidden — any mutation that changes persona state should invalidate
 * `['personas']` explicitly.
 *
 * No longer takes a `blueprintKind` filter. Personas are surface-agnostic;
 * the library shows everything visible regardless of artifact context.
 * Per-blueprint relevance, if it returns, will live as a soft sort, not
 * a hard filter on the wire.
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson } from '../../../lib/apiFetchJson';
import type { KnownSurface } from '../../../lib/skill-surfaces-client';
import { PersonaListItemSchema, type PersonaListItem } from '../../../types/personas';

const PersonaListSchema = z.array(PersonaListItemSchema);

/**
 * Fetch the visible persona list. When `surface` is supplied, the server
 * narrows to personas tagged for that surface (U2). The surface is part of the
 * React Query key so different surfaces don't collide in cache — fetching
 * `usePersonas('keystone-chat')` must not overwrite the unfiltered
 * `usePersonas()` cache entry (architect finding).
 */
export function usePersonas(surface?: KnownSurface) {
  const { apiFetch } = useApi();
  return useQuery<PersonaListItem[]>({
    queryKey: ['personas', surface ?? null],
    queryFn: async () => {
      const qs = surface ? `?surface=${encodeURIComponent(surface)}` : '';
      try {
        return await fetchJson(apiFetch, PersonaListSchema, `/api/v4/personas${qs}`);
      } catch (err) {
        // A non-2xx answer degrades to an empty library, as before.
        if (err instanceof ApiProblemError && err.kind === 'http') return [];
        throw err;
      }
    },
    staleTime: Infinity,
  });
}
